import {
	Button,
	Checkbox,
	ChipSelect,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	EmptyState,
	Input,
	Label,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
} from "@ondit/ui";
/**
 * The filing a customer does about what the eight detection screens read.
 *
 * Four sections, and they are four because they answer four different
 * questions. What kind of thing is this cited domain (categories). Which of my
 * campaigns is this page part of (tags). What do the answers call us that we
 * are not counting (name matching). Who are we measured against (competitors).
 *
 * They live on a tab of the brand's Settings screen rather than on a screen of
 * their own: none of it is a reading, all of it is a thing you set once and
 * revisit when a number looks wrong, and that is the same shelf the brand's
 * facts and its engines already sit on. Each section saves itself, which is
 * why this tab has no save bar — a category picked from a dropdown is a
 * decision made the moment it is picked.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api, CITATION_CATEGORIES } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

/** The kinds the reading pass can be told to resolve. */
const RESOLUTION_KINDS = ["companies", "products", "projects", "models"] as const;

export function InsightFiling({ entityId }: { entityId: string }) {
	return (
		<>
			<div className="filing-grid">
				<Rules entityId={entityId} />
				<Tags entityId={entityId} />
			</div>
			<Names entityId={entityId} />
		</>
	);
}

/**
 * How a cited domain is filed, when the derived answer is wrong.
 *
 * It is wrong about exactly the domains a customer cares about — their docs on
 * a different apex, a partner, a rival's blog on a shared platform — and one
 * misfiled row is enough for somebody to stop believing the other forty.
 */
function Rules({ entityId }: { entityId: string }) {
	const t = useT();
	const [busy, setBusy] = useState(false);
	const { locale } = useI18n();
	const [url, setUrl] = useState("");
	const [category, setCategory] = useState<string>("competitor");
	const [subpaths, setSubpaths] = useState(true);
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "citation-rules"],
		queryFn: () => api.citationRules(entityId),
		...read,
	});
	const addRule = useMutation({
		mutationFn: (rule: Parameters<typeof api.addCitationRule>[1]) => api.addCitationRule(entityId, rule),
		onSuccess: refresh,
	});
	const removeRule = useMutation({
		mutationFn: (id: string) => api.removeCitationRule(entityId, id),
		onSuccess: refresh,
	});

	async function add(event: React.FormEvent) {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		try {
			await addRule.mutateAsync({ url, category, subpaths });
			setUrl("");
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function remove(id: string) {
		if (busy) return;
		setBusy(true);
		try {
			await removeRule.mutateAsync(id);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Section title={t("insightSettings.rules")} lede={t("insightSettings.rulesHint")}>
			<form className="filing-form" onSubmit={add}>
				<div className="field">
					<Label htmlFor="filing-rule-url">URL</Label>
					<Input
						id="filing-rule-url"
						placeholder="example.com/blog"
						value={url}
						onChange={(event) => setUrl(event.target.value)}
						required
						maxLength={500}
					/>
				</div>
				<div className="row-actions">
					<ChipSelect
						value={category}
						onChange={setCategory}
						aria-label={t("citations.kind")}
						options={CITATION_CATEGORIES.map((kind) => ({ value: kind, label: t(`citations.kind.${kind}`) }))}
					/>
					{/* A rule with a path is about a page or a section, and this is which.
				    A rule with no path is about a site and always covers subdomains,
				    the way the built-in classification does. */}
					<label className="checkline" htmlFor="rule-subpaths">
						<Checkbox
							id="rule-subpaths"
							checked={subpaths}
							aria-label={t("insightSettings.subpaths")}
							onCheckedChange={(next) => setSubpaths(next === true)}
						/>
						{t("insightSettings.subpaths")}
					</label>
					<Button type="submit" variant="default" disabled={busy || !url.trim()}>
						<Plus aria-hidden />
						{t("common.add")}
					</Button>
				</div>
			</form>

			<Async state={state} empty={<EmptyState size="compact" title={t("insightSettings.noRules")} />}>
				{(rules) => (
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead className="wrap">URL</TableHead>
									<TableHead>{t("citations.kind")}</TableHead>
									<TableHead>{t("insightSettings.subpaths")}</TableHead>
									<TableHead>{t("common.created")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rules.map((rule) => (
									<TableRow key={rule.id}>
										<TableCell className="wrap mono">{rule.url}</TableCell>
										<TableCell>
											<span className="pill">{t(`citations.kind.${rule.category}`)}</span>
										</TableCell>
										<TableCell>{rule.subpaths ? t("common.yes") : t("common.no")}</TableCell>
										<TableCell className="mono">{fmt.relative(locale, rule.created_at) ?? "—"}</TableCell>
										<TableCell className="num">
											<Button
												variant="default"
												size="sm"
												aria-label={t("common.remove")}
												disabled={busy}
												onClick={() => remove(rule.id)}
											>
												<Trash2 aria-hidden />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</Async>
		</Section>
	);
}

/**
 * Tags on cited pages.
 *
 * A category answers "what kind of site is this" and a tag answers "which of my
 * campaigns is this". They are separate because one URL has exactly one of the
 * first and any number of the second, and folding them together produces a
 * category list that grows without bound.
 */
function Tags({ entityId }: { entityId: string }) {
	const t = useT();
	const [busy, setBusy] = useState(false);
	const { locale } = useI18n();
	const [url, setUrl] = useState("");
	const [tags, setTags] = useState("");
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "citation-tags"],
		queryFn: () => api.citationTags(entityId),
		...read,
	});
	const addTag = useMutation({
		mutationFn: (tag: Parameters<typeof api.addCitationTag>[1]) => api.addCitationTag(entityId, tag),
		onSuccess: refresh,
	});
	const removeTag = useMutation({
		mutationFn: (id: string) => api.removeCitationTag(entityId, id),
		onSuccess: refresh,
	});

	async function add(event: React.FormEvent) {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		try {
			await addTag.mutateAsync({
				url,
				tags: tags
					.split(",")
					.map((tag) => tag.trim())
					.filter(Boolean),
			});
			setUrl("");
			setTags("");
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function remove(id: string) {
		if (busy) return;
		setBusy(true);
		try {
			await removeTag.mutateAsync(id);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Section title={t("insightSettings.tags")} lede={t("insightSettings.tagsHint")}>
			<form className="filing-form" onSubmit={add}>
				<div className="field">
					<Label htmlFor="filing-tag-url">URL</Label>
					<Input
						id="filing-tag-url"
						placeholder="example.com/launch"
						value={url}
						onChange={(event) => setUrl(event.target.value)}
						required
						maxLength={500}
					/>
				</div>
				<div className="row-actions items-end">
					<div className="field flex-1">
						<Label htmlFor="filing-tag-values">{t("insightSettings.tags")}</Label>
						<Input
							id="filing-tag-values"
							placeholder={t("insightSettings.tagsPlaceholder")}
							value={tags}
							onChange={(event) => setTags(event.target.value)}
							required
						/>
					</div>
					<Button
						type="submit"
						variant="default"
						disabled={busy || !url.trim() || !tags.split(",").some((tag) => tag.trim())}
					>
						<Plus aria-hidden />
						{t("common.add")}
					</Button>
				</div>
			</form>

			<Async state={state} empty={<EmptyState size="compact" title={t("insightSettings.noTags")} />}>
				{(rules) => (
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead className="wrap">URL</TableHead>
									<TableHead>{t("insightSettings.tags")}</TableHead>
									<TableHead>{t("common.created")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rules.map((rule) => (
									<TableRow key={rule.id}>
										<TableCell className="wrap mono">{rule.url}</TableCell>
										<TableCell>
											{rule.tags.map((tag) => (
												<span className="pill pill-quiet" key={tag}>
													{tag}
												</span>
											))}
										</TableCell>
										<TableCell className="mono">{fmt.relative(locale, rule.created_at) ?? "—"}</TableCell>
										<TableCell className="num">
											<Button
												variant="default"
												size="sm"
												aria-label={t("common.remove")}
												disabled={busy}
												onClick={() => remove(rule.id)}
											>
												<Trash2 aria-hidden />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</Async>
		</Section>
	);
}

/**
 * What the answers call this brand, and what they call the ones we cannot see.
 *
 * Every row in the unmapped list is visibility this brand is not being credited
 * with: the matcher works on names and aliases, so a brand the assistants call
 * something else is absent from every number on the panel and absent in a way
 * nobody can notice.
 */
function Names({ entityId }: { entityId: string }) {
	const { editor } = useAccess();
	const t = useT();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "name-matching"],
		queryFn: () => api.nameMatching(entityId, { range: "90d" }),
		...read,
	});
	const mapName = useMutation({
		mutationFn: (choice: Parameters<typeof api.mapName>[1]) => api.mapName(entityId, choice),
		onSuccess: refresh,
	});
	const resolve = useMutation({
		mutationFn: (body: Parameters<typeof api.saveResolution>[1]) => api.saveResolution(entityId, body),
		onSuccess: refresh,
	});
	const [kinds, setKinds] = useState<string[] | null>(null);
	const [notes, setNotes] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function act(name: string, action: "map" | "track" | "dismiss") {
		if (busy) return;
		setBusy(true);
		try {
			await mapName.mutateAsync({ name, action });
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function saveResolution(current: { resolution_kinds: string[]; resolution_notes: string | null }) {
		setBusy(true);
		try {
			await resolve.mutateAsync({
				kinds: kinds ?? current.resolution_kinds,
				notes: notes ?? current.resolution_notes,
			});
			toast.success(t("common.saved"));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Async state={state}>
			{(data) => {
				const chosen = kinds ?? data.resolution_kinds;
				return (
					<>
						<Section title={t("insightSettings.names")} lede={t("insightSettings.namesHint")}>
							<div className="table-scroll">
								<Table variant="resource">
									<TableHeader>
										<TableRow>
											<TableHead className="wrap">{t("insightSettings.name")}</TableHead>
											<TableHead className="num">{t("insightSettings.mentions")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.mapped.map((row) => (
											<TableRow key={row.name}>
												<TableCell className="wrap">
													{row.name}
													{row.primary ? (
														<span className="pill pill-accent">{t("insightSettings.primary")}</span>
													) : null}
													{/* An alias that never matches is one somebody
													    guessed, and the zero is how they find out. */}
													{!row.primary && row.mentions === 0 ? (
														<span className="pill">{t("insightSettings.neverSeen")}</span>
													) : null}
												</TableCell>
												<TableCell className="num mono">{row.mentions}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Section>

						<Section title={t("insightSettings.unmapped")} lede={t("insightSettings.unmappedHint")}>
							{data.unmapped.length === 0 ? (
								<EmptyState size="compact" title={t("insightSettings.noUnmapped")} />
							) : (
								<div className="table-scroll">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<TableHead className="wrap">{t("insightSettings.name")}</TableHead>
												<TableHead>{t("table.domain")}</TableHead>
												<TableHead className="num">{t("insightSettings.mentions")}</TableHead>
												<TableHead>{t("insightSettings.lastSeen")}</TableHead>
												<TableHead />
											</TableRow>
										</TableHeader>
										<TableBody>
											{data.unmapped.map((row) => (
												<TableRow key={row.name}>
													<TableCell className="wrap">{row.name}</TableCell>
													<TableCell className="mono">{row.domain ?? "—"}</TableCell>
													<TableCell className="num mono">{row.mentions}</TableCell>
													<TableCell className="mono">{fmt.relative(locale, row.last_seen_at) ?? "—"}</TableCell>
													<TableCell className="num">
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button variant="default" size="sm" disabled={busy || !editor}>
																	{t("insightSettings.classify")}
																	<ChevronDown aria-hidden />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuItem disabled={busy || !editor} onSelect={() => act(row.name, "map")}>
																	{t("insightSettings.mapIt")}
																</DropdownMenuItem>
																<DropdownMenuItem
																	disabled={busy || !editor || !row.domain}
																	onSelect={() => act(row.name, "track")}
																>
																	{t("insightSettings.trackIt")}
																</DropdownMenuItem>
																<DropdownMenuItem disabled={busy || !editor} onSelect={() => act(row.name, "dismiss")}>
																	{t("insightSettings.dismissIt")}
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</Section>

						<Section title={t("insightSettings.resolution")} lede={t("insightSettings.resolutionHint")}>
							<div className="row-actions" style={{ marginBottom: "var(--s-7)" }}>
								{RESOLUTION_KINDS.map((kind) => (
									<label className="checkline" key={kind} htmlFor={`resolution-${kind}`}>
										<Checkbox
											id={`resolution-${kind}`}
											checked={chosen.includes(kind)}
											aria-label={t(`insightSettings.kind.${kind}`)}
											onCheckedChange={(next) =>
												setKinds(next === true ? [...chosen, kind] : chosen.filter((one) => one !== kind))
											}
										/>
										{t(`insightSettings.kind.${kind}`)}
									</label>
								))}
							</div>
							<Textarea
								maxLength={500}
								rows={3}
								aria-label={t("insightSettings.notesPlaceholder")}
								placeholder={t("insightSettings.notesPlaceholder")}
								value={notes ?? data.resolution_notes ?? ""}
								onChange={(event) => setNotes(event.target.value)}
							/>
							<div className="row-actions" style={{ marginTop: "var(--s-6)" }}>
								<Button
									type="button"
									disabled={
										busy ||
										(JSON.stringify(chosen) === JSON.stringify(data.resolution_kinds) &&
											(notes ?? data.resolution_notes ?? "") === (data.resolution_notes ?? ""))
									}
									onClick={() => saveResolution(data)}
								>
									{t("common.save")}
								</Button>
							</div>
						</Section>

						<Competitors entityId={entityId} tracked={data.tracked_domains} excluded={data.excluded_domains} />
					</>
				);
			}}
		</Async>
	);
}

/**
 * The two competitor lists, written together.
 *
 * One form, because a domain cannot be on both and a customer moving one from
 * the blacklist to the whitelist is making a single decision. Tracking wins
 * where they collide; the server enforces that, and this screen shows the
 * result rather than guessing at it.
 */
function Competitors({ entityId, tracked, excluded }: { entityId: string; tracked: string[]; excluded: string[] }) {
	const t = useT();
	const { locale } = useI18n();
	const [adding, setAdding] = useState("");
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const track = useMutation({
		mutationFn: (next: Parameters<typeof api.saveTracked>[1]) => api.saveTracked(entityId, next),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function save(next: { tracked_domains?: string[]; excluded_domains?: string[] }) {
		setBusy(true);
		try {
			await track.mutateAsync(next);
			setAdding("");
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Section title={t("insightSettings.competitors")} lede={t("insightSettings.competitorsHint")}>
			<form
				className="row-actions"
				onSubmit={(event) => {
					event.preventDefault();
					save({ tracked_domains: [...tracked, adding] });
				}}
				style={{ marginBottom: "var(--s-8)" }}
			>
				<Input
					className="w-full min-w-0 sm:w-auto sm:flex-1"
					aria-label={t("insightSettings.competitors")}
					placeholder="competitor.com"
					value={adding}
					onChange={(event) => setAdding(event.target.value)}
					required
				/>
				<Button type="submit" variant="default" disabled={busy || !adding.trim()}>
					<Plus aria-hidden />
					{t("insightSettings.track")}
				</Button>
			</form>

			<div className="two-up">
				<div>
					<h3>{t("insightSettings.tracked")}</h3>
					{tracked.length === 0 ? (
						<EmptyState size="compact" title={t("insightSettings.noTracked")} />
					) : (
						<ul className="chip-list">
							{tracked.map((domain) => (
								<li key={domain}>
									<span className="mono">{domain}</span>
									<Button
										variant="default"
										size="sm"
										aria-label={t("common.remove")}
										onClick={() => save({ tracked_domains: tracked.filter((one) => one !== domain) })}
									>
										<Trash2 aria-hidden />
									</Button>
								</li>
							))}
						</ul>
					)}
				</div>

				<div>
					<h3>{t("insightSettings.excluded")}</h3>
					{excluded.length === 0 ? (
						<EmptyState size="compact" title={t("insightSettings.noExcluded")} />
					) : (
						<ul className="chip-list">
							{excluded.map((domain) => (
								<li key={domain}>
									<span className="mono">{domain}</span>
									<Button
										variant="default"
										size="sm"
										aria-label={t("common.remove")}
										onClick={() => save({ excluded_domains: excluded.filter((one) => one !== domain) })}
									>
										<Trash2 aria-hidden />
									</Button>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</Section>
	);
}
